import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import {
  formatEur,
  formatDateShort,
  formatDuration,
  MISSION_STATUS_LABEL,
  MISSION_STATUS_COLOR,
  MISSION_TYPE_CONFIG,
} from '@/lib/format'
import { useAuth } from '@/lib/hooks/useAuth'
import { useSpecialtyConfig } from '@/lib/hooks/useSpecialtyConfig'

interface Mission {
  id: string
  title: string
  description: string
  type: string
  status: string
  date: string
  city: string
  duration: number
  amount: number
  _count: { assignments: number }
  assignments: Array<{
    provider: { id: string; firstName: string; lastName: string; rating: number }
  }>
}

const STATUS_TABS = [
  { key: 'ALL', label: 'Toutes' },
  { key: 'OPEN', label: 'Ouvertes' },
  { key: 'ASSIGNED', label: 'Assignées' },
  { key: 'IN_PROGRESS', label: 'En cours' },
  { key: 'COMPLETED', label: 'Terminées' },
  { key: 'CANCELLED', label: 'Annulées' },
]

function MissionCard({ mission, unread = 0 }: { mission: Mission; unread?: number }) {
  const assignedProvider = mission.assignments[0]?.provider
  const typeCfg = MISSION_TYPE_CONFIG[mission.type] ?? MISSION_TYPE_CONFIG.OTHER

  return (
    <Link to={`/missions/${mission.id}`} className="glass-card-hover p-5 flex flex-col gap-3 group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-base ${typeCfg.bg} border ${typeCfg.border}`}>
            {typeCfg.icon}
          </span>
          <h3 className="text-text-primary font-medium text-sm leading-snug line-clamp-2">
            {mission.title}
          </h3>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${MISSION_STATUS_COLOR[mission.status]}`}>
          {MISSION_STATUS_LABEL[mission.status]}
        </span>
      </div>

      <p className="text-text-muted text-xs line-clamp-2 leading-relaxed">{mission.description}</p>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-text-secondary">
          <span>📅</span>
          <span>{formatDateShort(mission.date)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-text-secondary">
          <span>⏱</span>
          <span>{formatDuration(mission.duration)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-text-secondary">
          <span>📍</span>
          <span>{mission.city}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${typeCfg.bg} ${typeCfg.border} ${typeCfg.color}`}>
            {typeCfg.icon} {typeCfg.label}
          </span>
        </div>
      </div>

      <hr className="glass-divider" />

      {unread > 0 && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent/10 border border-accent/20">
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse flex-shrink-0" />
          <span className="text-accent text-xs font-medium">
            {unread} message{unread > 1 ? 's' : ''} non lu{unread > 1 ? 's' : ''}
            {assignedProvider ? ` · ${assignedProvider.firstName} ${assignedProvider.lastName}` : ' · Prestataire'}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-text-primary font-semibold text-sm">{formatEur(mission.amount)}</span>
        <div className="flex items-center gap-3">
          {assignedProvider ? (
            <span className="text-success text-xs">
              {assignedProvider.firstName} {assignedProvider.lastName}
            </span>
          ) : (
            <span className="text-text-muted text-xs">
              {mission._count.assignments} candidature{mission._count.assignments !== 1 ? 's' : ''}
            </span>
          )}
          <span className="text-text-muted text-xs group-hover:text-text-secondary transition-colors">→</span>
        </div>
      </div>
    </Link>
  )
}

interface CnpasAvailableMission {
  id: string
  title: string
  description: string
  type: string
  date: string
  city: string
  address: string
  postalCode: string
  duration: number
  amount: number
  requiredSpecialties: { specialty: string; quantity: number }[] | null
  company: { id: string; name: string; logoUrl: string | null }
}

function CnpasAvailableCard({
  mission,
  onSelect,
}: {
  mission: CnpasAvailableMission
  onSelect: (m: CnpasAvailableMission) => void
}) {
  const { byKey: specialtyByKey } = useSpecialtyConfig()
  const typeCfg = MISSION_TYPE_CONFIG[mission.type] ?? MISSION_TYPE_CONFIG.OTHER
  return (
    <button
      onClick={() => onSelect(mission)}
      className="glass-card p-5 flex flex-col gap-3 border-blue-500/20 text-left hover:border-blue-400/40 transition-all group w-full"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-base ${typeCfg.bg} border ${typeCfg.border}`}>
            {typeCfg.icon}
          </span>
          <div className="min-w-0">
            <p className="text-text-muted text-[10px]">{mission.company.name}</p>
            <h3 className="text-text-primary font-medium text-sm leading-snug line-clamp-1">{mission.title}</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className="text-blue-300 font-bold text-sm">{formatEur(mission.amount)}</p>
          <span className="text-text-muted text-xs group-hover:text-text-secondary transition-colors">→</span>
        </div>
      </div>
      <p className="text-text-muted text-xs line-clamp-2 leading-relaxed">{mission.description}</p>
      <div className="flex items-center gap-3 flex-wrap text-xs text-text-secondary">
        <span>📅 {formatDateShort(mission.date)}</span>
        <span>⏱ {formatDuration(mission.duration)}</span>
        <span>📍 {mission.city}</span>
      </div>
      {mission.requiredSpecialties && mission.requiredSpecialties.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {mission.requiredSpecialties.map((s) => {
            const info = specialtyByKey.get(s.specialty)
            return (
              <span key={s.specialty} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 border border-blue-500/20 text-blue-300">
                {info?.icon} {info?.shortLabel ?? s.specialty} × {s.quantity}
              </span>
            )
          })}
        </div>
      )}
    </button>
  )
}

function CnpasModal({
  mission,
  onClose,
  onAccepted,
}: {
  mission: CnpasAvailableMission
  onClose: () => void
  onAccepted: (newMissionId: string) => void
}) {
  const navigate = useNavigate()
  const { byKey: specialtyByKey } = useSpecialtyConfig()
  const [amount, setAmount] = useState(Math.round(mission.amount / 100))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function handleAccept() {
    if (!amount || amount < 1) { setError('Budget requis (min. 1 €)'); return }
    setSubmitting(true); setError('')
    try {
      const res = await apiFetch<{ id: string }>(`/missions/cnpas/prendre/${mission.id}`, {
        method: 'POST',
        body: JSON.stringify({ amount }),
      })
      onAccepted(res.id)
      navigate(`/missions/${res.id}`)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative glass-card w-full max-w-lg p-6 space-y-5 shadow-2xl"
        style={{ background: 'rgba(10,10,20,0.95)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-text-muted text-xs mb-1">{mission.company.name}</p>
            <h2 className="text-text-primary font-bold text-lg leading-snug">{mission.title}</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xl flex-shrink-0 transition-colors">✕</button>
        </div>

        {/* Details */}
        <p className="text-text-secondary text-sm leading-relaxed">{mission.description}</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2 text-text-secondary"><span>📅</span> {formatDateShort(mission.date)}</div>
          <div className="flex items-center gap-2 text-text-secondary"><span>⏱</span> {formatDuration(mission.duration)}</div>
          <div className="flex items-center gap-2 text-text-secondary"><span>📍</span> {mission.address}, {mission.city} {mission.postalCode}</div>
          <div className="flex items-center gap-2 text-text-secondary"><span>💰</span> Budget client : <span className="text-blue-300 font-semibold">{formatEur(mission.amount)}</span></div>
        </div>

        {mission.requiredSpecialties && mission.requiredSpecialties.length > 0 && (
          <div>
            <p className="text-text-muted text-xs mb-2">Profil recherché</p>
            <div className="flex gap-1.5 flex-wrap">
              {mission.requiredSpecialties.map((s) => {
                const info = specialtyByKey.get(s.specialty)
                return (
                  <span key={s.specialty} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 border border-blue-500/20 text-blue-300">
                    {info?.icon} {info?.shortLabel ?? s.specialty} × {s.quantity}
                  </span>
                )
              })}
            </div>
          </div>
        )}

        <hr className="glass-divider" />

        {/* Mon budget pour mes ADS */}
        <div>
          <label className="glass-label">Mon budget pour mes prestataires ADS (€)</label>
          <p className="text-text-muted text-xs mb-2">Ce montant sera affiché à vos prestataires ADS.</p>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            min={1}
            className="glass-input"
          />
        </div>

        {error && <p className="text-danger text-sm">{error}</p>}

        {/* CTAs */}
        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            disabled={submitting}
            className="glass-btn-accent flex-1 py-3 text-sm rounded-xl font-medium"
          >
            {submitting ? 'Envoi en cours…' : '🛡️ Envoyer à mes prestataires ADS'}
          </button>
          <button
            onClick={onClose}
            className="glass-btn px-5 py-3 text-sm rounded-xl text-text-muted"
          >
            Refuser
          </button>
        </div>
      </div>
    </div>
  )
}

export default function MissionsPage() {
  useAuth('COMPANY')
  const [missions, setMissions] = useState<Mission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('ALL')
  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({})
  const [cnpasMissions, setCnpasMissions] = useState<CnpasAvailableMission[]>([])
  const [cnpasLoading, setCnpasLoading] = useState(false)
  const [isCnpas, setIsCnpas] = useState<boolean | null>(null)
  const [selectedCnpasMission, setSelectedCnpasMission] = useState<CnpasAvailableMission | null>(null)

  useEffect(() => {
    apiFetch<Mission[]>('/companies/me/missions')
      .then(setMissions)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))

    apiFetch<{ missionId: string; count: number }[]>('/missions/messages/unread-per-mission')
      .then((data) => {
        const m: Record<string, number> = {}
        for (const d of data) m[d.missionId] = d.count
        setUnreadMap(m)
      })
      .catch(() => {})

    // Vérification live du statut CNPAS (ne pas se fier au JWT qui peut être périmé)
    apiFetch<{ isCnpas: boolean }>('/companies/me')
      .then((data) => setIsCnpas(data.isCnpas ?? false))
      .catch(() => setIsCnpas(false))
  }, [])

  useEffect(() => {
    if (!isCnpas) return
    setCnpasLoading(true)
    apiFetch<CnpasAvailableMission[]>('/missions/cnpas/disponibles')
      .then(setCnpasMissions)
      .catch(() => {})
      .finally(() => setCnpasLoading(false))
  }, [isCnpas])

  const filtered =
    activeTab === 'ALL' ? missions : missions.filter((m) => m.status === activeTab)

  const counts = STATUS_TABS.reduce(
    (acc, tab) => {
      acc[tab.key] =
        tab.key === 'ALL' ? missions.length : missions.filter((m) => m.status === tab.key).length
      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Mes missions</h1>
          <p className="text-text-secondary text-sm mt-1">{missions.length} mission{missions.length !== 1 ? 's' : ''} au total</p>
        </div>
        <Link to="/missions/nouvelle" className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl inline-flex items-center gap-2">
          + Nouvelle
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={[
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 border',
              activeTab === tab.key
                ? 'bg-white/[0.10] border-white/[0.15] text-text-primary'
                : 'bg-transparent border-transparent text-text-secondary hover:text-text-primary hover:bg-white/[0.05]',
            ].join(' ')}
          >
            {tab.label}
            {counts[tab.key] > 0 && (
              <span
                className={[
                  'inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px]',
                  activeTab === tab.key ? 'bg-accent/20 text-accent' : 'bg-white/[0.08] text-text-muted',
                ].join(' ')}
              >
                {counts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="glass-card p-4 border-danger/25 bg-danger/10 text-danger text-sm mb-6">
          {error}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="glass-card h-48 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-4xl mb-4">📭</p>
          <p className="text-text-primary font-medium mb-2">Aucune mission {activeTab !== 'ALL' ? MISSION_STATUS_LABEL[activeTab]?.toLowerCase() : ''}</p>
          {activeTab === 'ALL' && (
            <Link to="/missions/nouvelle" className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block mt-4">
              Créer ma première mission →
            </Link>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {filtered.map((m) => (
            <MissionCard key={m.id} mission={m} unread={unreadMap[m.id] ?? 0} />
          ))}
        </div>
      )}

      {/* Section CNPAS : demandes ADS/SSIAP disponibles */}
      {isCnpas && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">🛡️</span>
              <h2 className="text-lg font-semibold text-text-primary">Demandes ADS / SSIAP disponibles</h2>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 border border-blue-500/25 text-blue-300">
              CNPAS
            </span>
            {cnpasMissions.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/[0.08] border border-white/[0.12] text-text-secondary">
                {cnpasMissions.length} disponible{cnpasMissions.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-text-muted text-xs mb-4">
            Ces entreprises recherchent une société CNPAS pour leurs besoins en sécurité.
          </p>

          {cnpasLoading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[1, 2].map((i) => <div key={i} className="glass-card h-36 animate-pulse" />)}
            </div>
          ) : cnpasMissions.length === 0 ? (
            <div className="glass-card p-10 text-center">
              <p className="text-3xl mb-3">🔍</p>
              <p className="text-text-secondary text-sm">Aucune demande ADS/SSIAP disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {cnpasMissions.map((m) => (
                <CnpasAvailableCard key={m.id} mission={m} onSelect={setSelectedCnpasMission} />
              ))}
            </div>
          )}
          {selectedCnpasMission && (
            <CnpasModal
              mission={selectedCnpasMission}
              onClose={() => setSelectedCnpasMission(null)}
              onAccepted={(_id) => {
                setCnpasMissions((prev) => prev.filter((m) => m.id !== selectedCnpasMission.id))
                setSelectedCnpasMission(null)
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
